import { useState, useContext, useEffect } from 'react'
import { useController, useForm } from 'react-hook-form'

import { type SanityReviewsStrings } from '@data/sanity/queries/types/modules'
import { type SanityProductReviewFragment } from '@data/sanity/queries/types/product'
import { getSanityImageUrl } from '@lib/image'
import { defaultLocale } from '@lib/language'
import { LanguageContext } from '@lib/language-context'
import { type StampedReviewInput, createStampedReview } from '@lib/review'
import { getPageUrl } from '@lib/routes'
import { SiteContext } from '@lib/site-context'
import { StringsContext } from '@lib/strings-context'

import Alert from '@components/alert'
import Button, { ButtonVariant } from '@components/buttons/button'
import InputField from '@components/input-field'
import Stars from '@components/stars'
import TextArea from '@components/textarea'
import ComplexPortableText from '@components/complex-portable-text'

interface ReviewFormProps {
  reviewsStrings: SanityReviewsStrings
  product: SanityProductReviewFragment
  className?: string
  onCancelClick?: () => void
}

type FormValues = StampedReviewInput

/**
 * Review form field hook.
 */
const useReviewForm = (
  product: SanityProductReviewFragment,
  reviewsStrings: SanityReviewsStrings,
) => {
  const { settings } = useContext(SiteContext)

  const productUrl = getPageUrl(defaultLocale, 'product', product.slug.current)
  const defaultValues: FormValues = {
    author: '',
    email: '',
    reviewTitle: '',
    reviewMessage: '',
    reviewRating: 0,
    productId: product.productID,
    productName: product.title,
    productSKU: product.sku,
    productImageUrl: getSanityImageUrl(product.listingPhotos?.[0].galleryImage),
    productUrl: `${settings?.siteURL}${productUrl}`,
  }

  const {
    handleSubmit,
    control,
    register,
    reset,
    setValue,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues,
  })

  // Define all form fields
  const authorControl = useController<FormValues, 'author'>({
    name: 'author',
    control,
    rules: {
      required: {
        value: true,
        message: reviewsStrings.reviewsFormAuthorMissing,
      },
    },
  })
  const emailControl = useController<FormValues, 'email'>({
    name: 'email',
    control,
    rules: {
      required: {
        value: true,
        message: reviewsStrings.reviewsFormEmailMissing,
      },
    },
  })
  const reviewTitleControl = useController<FormValues, 'reviewTitle'>({
    name: 'reviewTitle',
    control,
    rules: {
      required: {
        value: true,
        message: reviewsStrings.reviewsFormReviewTitleMissing,
      },
    },
  })
  const reviewMessageControl = useController<FormValues, 'reviewMessage'>({
    name: 'reviewMessage',
    control,
    rules: {
      required: {
        value: true,
        message: reviewsStrings.reviewsFormReviewMessageMissing,
      },
    },
  })
  const reviewRatingControl = useController<FormValues, 'reviewRating'>({
    name: 'reviewRating',
    control,
    rules: {
      required: {
        value: true,
        message: reviewsStrings.reviewsFormReviewRatingMissing,
      },
      min: { value: 1, message: reviewsStrings.reviewsFormReviewRatingMissing },
      max: { value: 5, message: reviewsStrings.reviewsFormReviewRatingMissing },
    },
  })
  register('productId')
  register('productName')
  register('productSKU')
  register('productImageUrl')
  register('productUrl')

  return {
    handleSubmit,
    reset,
    setValue,
    errors,
    isSubmitSuccessful,
    controls: {
      author: authorControl,
      email: emailControl,
      reviewTitle: reviewTitleControl,
      reviewMessage: reviewMessageControl,
      reviewRating: reviewRatingControl,
    },
  }
}

const ReviewForm = ({
  reviewsStrings,
  product,
  className,
  onCancelClick = () => null,
}: ReviewFormProps) => {
  const { locale } = useContext(LanguageContext)
  const strings = useContext(StringsContext)

  const {
    handleSubmit,
    reset,
    setValue,
    errors,
    isSubmitSuccessful,
    controls,
  } = useReviewForm(product, reviewsStrings)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Reset form on successful submission
  useEffect(() => {
    if (isSubmitSuccessful) {
      reset()
    }
  }, [isSubmitSuccessful, reset])

  // Handle form submission
  const onSubmit = async (data: FormValues) => {
    setIsError(false)
    setIsSuccess(false)
    setIsLoading(true)

    const reviewCreated = await createStampedReview(locale, data)

    setIsLoading(false)

    if (!reviewCreated) {
      setIsError(true)
    }

    if (reviewCreated) {
      setIsSuccess(true)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={className}>
      <div className="grid grid-cols-12 gap-x-5 gap-y-5">
        <div className="col-span-12 sm:col-span-6 lg:col-span-3">
          <InputField
            id="review-author"
            type="text"
            formRegister={controls.author.field}
            value={controls.author.field.value}
            errorMessage={errors.author?.message}
            label={reviewsStrings.reviewsFormAuthorInputLabel}
            placeholder={reviewsStrings.reviewsFormAuthorInputPlaceholder}
          />
        </div>
        <div className="col-span-12 sm:col-span-6 lg:col-span-3">
          <InputField
            id="review-email"
            type="email"
            formRegister={controls.email.field}
            value={controls.email.field.value}
            errorMessage={errors.email?.message}
            label={reviewsStrings.reviewsFormEmailInputLabel}
            placeholder={reviewsStrings.reviewsFormEmailInputPlaceholder}
          />
        </div>
        <div className="col-span-12">
          <label htmlFor="review-rating" className="text-xs block mb-2">
            {reviewsStrings.reviewsFormReviewRatingInputLabel}
          </label>
          <Stars
            ratingValue={controls.reviewRating.field.value}
            onClick={(value) =>
              setValue('reviewRating', value, { shouldValidate: true })
            }
            size={22}
          />
          {errors.reviewRating?.message && (
            <span role="alert" className="mt-2 text-xs text-error block">
              {errors.reviewRating?.message}
            </span>
          )}
        </div>
        <div className="col-span-12 lg:col-span-6">
          <InputField
            id="review-title"
            type="text"
            formRegister={controls.reviewTitle.field}
            value={controls.reviewTitle.field.value}
            errorMessage={errors.reviewTitle?.message}
            label={reviewsStrings.reviewsFormReviewTitleInputLabel}
            placeholder={reviewsStrings.reviewsFormReviewTitleInputPlaceholder}
          />
        </div>
        <div className="col-span-12">
          <TextArea
            formRegister={controls.reviewMessage.field}
            value={controls.reviewMessage.field.value}
            errorMessage={errors.reviewMessage?.message}
            label={reviewsStrings.reviewsFormReviewMessageInputLabel}
            placeholder={
              reviewsStrings.reviewsFormReviewMessageInputPlaceholder
            }
            className="min-h-[300px]"
          />
        </div>

        {isError && (
          <div key="error" className="col-span-12">
            <Alert error className="rc rc-alert rc-error">
              <ComplexPortableText
                content={reviewsStrings.reviewsFormErrorMessage}
              />
            </Alert>
          </div>
        )}

        {isSuccess && (
          <div key="success" className="col-span-12">
            <Alert success className="rc rc-alert rc-success">
              <ComplexPortableText
                content={reviewsStrings.reviewsFormSuccessMessage}
              />
            </Alert>
          </div>
        )}

        <div className="col-span-12">
          <div className="flex items-center justify-end">
            <Button
              variant={ButtonVariant.OUTLINED}
              onClick={(event) => {
                event.preventDefault()
                onCancelClick()
              }}
              className="min-w-[180px] mr-4"
            >
              {strings.buttonCancel}
            </Button>

            <Button
              type="submit"
              variant={ButtonVariant.FILLED}
              disabled={isLoading}
              className="min-w-[180px]"
            >
              {isLoading
                ? strings.buttonSubmitting
                : reviewsStrings.reviewsFormSubmit}
            </Button>
          </div>
        </div>
      </div>
    </form>
  )
}

export default ReviewForm
